import { isSameDay, isValid, parseISO, startOfDay, subDays } from "date-fns";
import { enGB, enUS } from "date-fns/locale";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { ref, shallowRef, type Ref } from "vue";

/*
 * The tenant calendar: the time zone and locale the backend uses to interpret the
 * `yyyy-MM-dd` date ranges the UI sends. Everything that decides "what day is it",
 * "when does the week start" or "how do we show this instant" goes through here so
 * that the browser's own zone never leaks into tenant-facing dates.
 *
 * Two kinds of Date flow through the app:
 *  - instants (API timestamps such as RecordingStart, lastScoredAt): real points in
 *    time, shown with formatInTenantZone().
 *  - tenant wall-clock dates (tenantNow(), tenantToday(), period boundaries, picker
 *    values): Dates whose *local* fields hold the tenant's calendar date/time. They
 *    are only ever used for calendar arithmetic and formatTenantWallClock(), and go
 *    on the wire as `yyyy-MM-dd` or formatLocalDateTime() wall-clock strings.
 */

export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface ITenantCalendarSettings {
  timeZone: string;
  locale: string;
  weekStartsOn: WeekStartsOn;
}

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const isValidTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
};

// No tenant default lives here: the backend resolves it (TenantCalendar) and always sends
// both values with the user profile. Without one -- before any profile has loaded, or a value
// this browser can't use -- dates follow the browser, as they did before tenant time zones.
const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;
const browserLocale = (): string => Intl.DateTimeFormat().resolvedOptions().locale;

export const resolveTimeZone = (timeZone?: string | null): string => {
  const trimmed = timeZone?.trim();
  return trimmed && isValidTimeZone(trimmed) ? trimmed : browserTimeZone();
};

export const resolveLocale = (locale?: string | null): string => {
  const trimmed = locale?.trim();
  if (!trimmed) return browserLocale();

  try {
    return Intl.getCanonicalLocales(trimmed)[0] ?? browserLocale();
  } catch {
    return browserLocale();
  }
};

// Only for when there is no first day from the backend (before a profile has loaded, or an
// older backend): en-US weeks start on Sunday, every other locale on Monday. The backend
// derives the real one from the locale's culture and sends it with the profile.
export const getWeekStartsOnForLocale = (locale?: string | null): WeekStartsOn => {
  return resolveLocale(locale).toLowerCase() === "en-us" ? 0 : 1;
};

// The backend's first day of the week: a DayOfWeek name ("Sunday") or its number (0-6).
export const resolveWeekStartsOn = (firstDayOfWeek: string | number | null | undefined, locale?: string | null): WeekStartsOn => {
  const index = typeof firstDayOfWeek === "number"
    ? firstDayOfWeek
    : /^\d$/.test(firstDayOfWeek?.trim() ?? "")
      ? Number(firstDayOfWeek)
      : DAY_NAMES.indexOf(firstDayOfWeek?.trim().toLowerCase() ?? "");
  return index >= 0 && index <= 6 ? (index as WeekStartsOn) : getWeekStartsOnForLocale(locale);
};

export const getDateFnsLocale = (locale?: string | null): Locale => {
  return resolveLocale(locale).toLowerCase() === "en-us" ? enUS : enGB;
};

const readStorage = (key: string): string | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
};

// The keys userProfileStore writes the profile's values under.
const readStoredSettings = (): ITenantCalendarSettings => {
  const locale = resolveLocale(readStorage("tenantLocale"));
  return {
    timeZone: resolveTimeZone(readStorage("tenantTimeZone")),
    locale,
    weekStartsOn: resolveWeekStartsOn(readStorage("tenantFirstDayOfWeek"), locale),
  };
};

// Reactive so computed() values and templates re-render once the profile loads.
// Hydrated from localStorage (written by userProfileStore) on first use.
const settings = ref<ITenantCalendarSettings | null>(null);

export const getTenantCalendarSettings = (): ITenantCalendarSettings => {
  if (!settings.value) {
    settings.value = readStoredSettings();
  }
  return settings.value;
};

// Called by userProfileStore when the profile is loaded. Persistence lives in the store.
export const setTenantCalendarSettings = (
  value: { timeZone?: string | null; locale?: string | null; firstDayOfWeek?: string | number | null } | null | undefined
): void => {
  const locale = resolveLocale(value?.locale);
  settings.value = {
    timeZone: resolveTimeZone(value?.timeZone),
    locale,
    weekStartsOn: resolveWeekStartsOn(value?.firstDayOfWeek, locale),
  };
  if (currentTenantDay.value) refreshTenantDay();
};

// Drops the cached settings so the next read re-hydrates from localStorage. For tests: the app has
// no sign-out of its own, and needs none here -- the auth middleware reloads the profile, and with
// it these settings, before every route renders, so a previous tenant's zone is never shown.
export const resetTenantCalendarSettings = (): void => {
  settings.value = null;
  stopTenantDayClock();
};

// A REACTIVE tenant today. computed() cannot see the clock, so one that calls tenantToday()
// keeps the day it first saw: a picker left open across the tenant's midnight would still cap
// at yesterday. Reading this ref instead re-evaluates when the tenant's day rolls over -- one
// shared minute timer checks for that -- or when the tenant's zone changes.
const currentTenantDay = shallowRef<Date | null>(null);
let tenantDayClock: ReturnType<typeof setInterval> | null = null;

const refreshTenantDay = (): void => {
  const today = tenantToday();
  if (!currentTenantDay.value || !isSameDay(currentTenantDay.value, today)) {
    currentTenantDay.value = today;
  }
};

export const useTenantToday = (): Readonly<Ref<Date>> => {
  refreshTenantDay();
  if (!tenantDayClock && typeof window !== "undefined") {
    tenantDayClock = setInterval(refreshTenantDay, 60_000);
  }
  return currentTenantDay as Readonly<Ref<Date>>;
};

const stopTenantDayClock = (): void => {
  if (tenantDayClock) clearInterval(tenantDayClock);
  tenantDayClock = null;
  currentTenantDay.value = null;
};

export const tenantTimeZone = (): string => getTenantCalendarSettings().timeZone;
export const tenantLocale = (): string => getTenantCalendarSettings().locale;
export const tenantWeekStartsOn = (): WeekStartsOn => getTenantCalendarSettings().weekStartsOn;
export const tenantDateFnsLocale = (): Locale => getDateFnsLocale(tenantLocale());

// The tenant's current wall clock, as a Date whose local fields are the tenant's.
export const tenantNow = (now: Date | number = Date.now()): Date => {
  return utcToZonedTime(now, tenantTimeZone());
};

// Midnight of the tenant's current calendar day (a wall-clock Date).
export const tenantToday = (now: Date | number = Date.now()): Date => {
  return startOfDay(tenantNow(now));
};

export const isTenantFirstDayOfWeek = (now: Date | number = Date.now()): boolean => {
  return tenantNow(now).getDay() === tenantWeekStartsOn();
};

// Converts a UTC instant to the tenant wall clock (for calendar comparisons of instants).
export const toTenantWallClock = (instant: Date | string | number): Date => {
  return utcToZonedTime(toInstant(instant), tenantTimeZone());
};

/*
 * API instants are UTC. The backend serialises them with a Z, but an offset-less ISO
 * timestamp is treated as UTC too (matching the backend's Unspecified => UTC convention)
 * rather than as browser-local. Only use this for values known to be instants; a
 * date-only `yyyy-MM-dd` is a calendar date, see parseCalendarDate().
 */
const offsetPattern = /(Z|[+-]\d{2}:?\d{2})$/i;
const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}T/;

export const toInstant = (value: Date | string | number): Date => {
  if (value instanceof Date || typeof value === "number") return new Date(value);
  const trimmed = value.trim();
  if (isoDateTimePattern.test(trimmed) && !offsetPattern.test(trimmed)) {
    return parseISO(`${trimmed}Z`);
  }
  return isoDateTimePattern.test(trimmed) ? parseISO(trimmed) : new Date(trimmed);
};

// A tenant-local calendar date from the API (`yyyy-MM-dd`, or a timestamp whose date part
// is the calendar day). Returns local midnight of that day: no zone shift.
export const parseCalendarDate = (value: Date | string): Date => {
  if (value instanceof Date) return startOfDay(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return startOfDay(new Date(value));
};

// Formats a UTC instant on the tenant's wall clock with the tenant's locale.
export const formatInTenantZone = (instant: Date | string | number, pattern: string): string => {
  const date = toInstant(instant);
  if (!isValid(date)) return "";
  return formatInTimeZone(date, tenantTimeZone(), pattern, { locale: tenantDateFnsLocale() });
};

// The tenant zone's UTC offset at the given instant, e.g. "+01:00", "-05:00" or "Z".
export const tenantTimeZoneOffset = (instant: Date | string | number = Date.now()): string => {
  return formatInTenantZone(instant, "XXX");
};

// Short zone name at the given instant, e.g. "BST", "GMT", "CDT" (or "GMT-5" where none exists).
export const tenantTimeZoneName = (instant: Date | string | number = Date.now()): string => {
  return formatInTenantZone(instant, "zzz");
};

export const isTenantToday = (calendarDate: Date): boolean => isSameDay(calendarDate, tenantToday());
export const isTenantYesterday = (calendarDate: Date): boolean => isSameDay(calendarDate, subDays(tenantToday(), 1));

// Schedule start and end dates are tenant calendar days, both inclusive: the backend's window
// runs from local midnight on the start day to local midnight after the end day.
export const hasTenantDayStarted = (calendarDate: Date | string): boolean => parseCalendarDate(calendarDate) <= tenantToday();
export const hasTenantDayEnded = (calendarDate: Date | string): boolean => parseCalendarDate(calendarDate) < tenantToday();

export const getTenantMonthName = (
  monthNumber: number,
  month: "long" | "short" | "narrow" | "numeric" | "2-digit" = "long"
): string => {
  return new Date(2020, monthNumber, 1).toLocaleString(tenantLocale(), { month });
};
